import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { formatCurrency } from '@/lib/utils'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { StageBadge } from '@/components/shared/StageBadge'
import { ProgressStepper } from '@/components/shared/ProgressStepper'

type LicenseRef = { id: string; display_name: string; type_name: string; business_name: string; account_name: string }
type RecordData = {
  id: string
  stage: string
  period_start_date: string
  period_end_date: string
  due_date: string
  overdue: boolean
  created_at: string
  payment: { id: string; service_fee_cents: number; gov_fee_cents: number; total_cents: number; currency: string; paid: boolean; paid_at: string | null; invoice?: { number: string; issued_at: string }; receipt?: { number: string; issued_at: string; payment_method: string } } | null
  filing: { id: string; reference_number: string | null; submitted_at: string | null; accepted: boolean; rejected: boolean; pending: boolean; rejection_reason: string | null } | null
  completion: { id: string; created_at: string } | null
  application_documents: { id: string; name: string; file_url: string | null; created_at: string }[]
  filing_documents: { id: string; name: string; file_url: string | null; created_at: string }[]
  completion_documents: { id: string; name: string; file_url: string | null; created_at: string }[]
}

type Props = {
  license: LicenseRef
  record: RecordData
}

const STAGE_KEYS = ['application', 'payment', 'filing', 'completed'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  payment: 'Awaiting Payment',
  filing: 'Filing',
  completed: 'Complete',
}

export default function BackofficeLicenseRecordShow({ license, record }: Props) {
  const confirm = useConfirm()
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [refNumber, setRefNumber] = useState('')

  const handleFilingSubmit = () => {
    router.post(`/backoffice/licenses/${license.id}/records/${record.id}/filing`, { reference_number: refNumber })
  }

  const handleAccept = async () => {
    if (!await confirm({ title: 'Accept Filing?', description: 'This will mark the filing as accepted.' })) return
    router.post(`/backoffice/licenses/${license.id}/records/${record.id}/filing/acceptance`)
  }

  const handleRejectConfirm = () => {
    if (!rejectionReason.trim()) return
    router.post(`/backoffice/licenses/${license.id}/records/${record.id}/filing/rejection`, { reason: rejectionReason.trim() })
    setShowRejectForm(false)
    setRejectionReason('')
  }

  const handleComplete = async () => {
    if (!await confirm({ title: 'Mark as Complete?', description: 'Mark this license period as complete?' })) return
    router.post(`/backoffice/licenses/${license.id}/records/${record.id}/completion`)
  }

  const handleManualPayment = async () => {
    if (!record.payment) return
    if (!await confirm({ title: 'Confirm Payment?', description: 'This will manually confirm payment as received.' })) return
    router.post(`/backoffice/licenses/${license.id}/records/${record.id}/payments/${record.payment.id}/payment_confirmation`)
  }

  return (
    <BackofficeLayout
      title={`${license.display_name} - Period`}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Licenses', href: '/backoffice/licenses' },
        { label: license.display_name, href: `/backoffice/licenses/${license.id}` },
        { label: `${record.period_start_date} - ${record.period_end_date}` },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">
              {record.period_start_date} — {record.period_end_date}
            </h1>
            <StageBadge stage={record.stage} variant="license" />
          </div>
          <p className="text-sm text-muted-foreground">{license.business_name} · {license.account_name}</p>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <span className="text-muted-foreground">Due: {new Date(record.due_date).toLocaleDateString()}</span>
            {record.overdue && (
              <span className="text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Overdue
              </span>
            )}
          </div>
        </div>

        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={record.stage} />
        </div>

        <div className="space-y-6">
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Payment</h2>
              <div className="flex items-center gap-3">
                {record.payment && record.payment.invoice && (
                  <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                    <Link href={`/backoffice/licenses/${license.id}/records/${record.id}/payments/${record.payment.id}/invoice`}>
                      View Invoice
                    </Link>
                  </Button>
                )}
                {record.payment && !record.payment.paid && (
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={handleManualPayment}>
                    Confirm Payment
                  </Button>
                )}
                {record.payment?.paid && (
                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                    <CheckCircle2 className="w-3 h-3" />Paid
                  </span>
                )}
              </div>
            </div>
            <div className="px-4 py-3">
              {record.payment ? (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service Fee</span>
                    <span>{formatCurrency(record.payment.service_fee_cents)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Gov Fee</span>
                    <span>{formatCurrency(record.payment.gov_fee_cents)}</span>
                  </div>
                  <div className="h-px bg-border my-1.5" />
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total</span>
                    <span>{formatCurrency(record.payment.total_cents)}</span>
                  </div>
                  {record.payment.invoice && (
                    <div className="flex justify-between text-xs text-muted-foreground pt-1">
                      <span>Invoice</span>
                      <span className="font-mono">{record.payment.invoice.number}</span>
                    </div>
                  )}
                  {record.payment.receipt && (
                    <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                      <span>Receipt</span>
                      <span className="font-mono">{record.payment.receipt.number}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Not paid</p>
              )}
            </div>
          </section>

          {/* Filing Submission */}
          {!record.filing && record.payment?.paid && (
            <section className="border rounded-lg px-4 py-3">
              <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-2">Submit Filing</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Reference number (optional)"
                  value={refNumber}
                  onChange={e => setRefNumber(e.target.value)}
                  className="flex-1 h-8 text-sm border rounded-md px-3 focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <Button size="sm" className="h-8 text-xs" onClick={handleFilingSubmit}>Submit Filing</Button>
              </div>
            </section>
          )}

          {record.filing && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Filing Status</p>
                <div className="flex items-center gap-2">
                  {record.filing.accepted && <Badge className="bg-green-600 text-white font-normal text-xs">Accepted</Badge>}
                  {record.filing.rejected && <Badge className="bg-red-600 text-white font-normal text-xs">Rejected</Badge>}
                  {record.filing.pending && <Badge className="bg-blue-600 text-white font-normal text-xs">Pending</Badge>}
                </div>
              </div>
              <div className="space-y-1.5">
                {record.filing.reference_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Reference</span>
                    <span>{record.filing.reference_number}</span>
                  </div>
                )}
                {record.filing.submitted_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Submitted</span>
                    <span>{new Date(record.filing.submitted_at).toLocaleDateString()}</span>
                  </div>
                )}
                {record.filing.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                    {record.filing.rejection_reason}
                  </div>
                )}
              </div>
              {!record.filing.accepted && !record.filing.rejected && record.payment?.paid && (
                <div className="mt-3 pt-2 border-t space-y-3">
                  {!showRejectForm ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleAccept} className="h-7 text-xs">Accept</Button>
                      <Button variant="outline" size="sm" onClick={() => setShowRejectForm(true)} className="h-7 text-xs text-red-600">Reject</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Rejection Reason</p>
                      <textarea
                        className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                        rows={3}
                        placeholder="Enter rejection reason..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleRejectConfirm} disabled={!rejectionReason.trim()} className="h-7 text-xs text-red-600">
                          Confirm Rejection
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setShowRejectForm(false); setRejectionReason('') }} className="h-7 text-xs">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {!record.completion && record.filing?.accepted && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Mark as Complete</p>
                <Button size="sm" onClick={handleComplete} className="h-7 text-xs">Complete</Button>
              </div>
            </section>
          )}

          {record.completion && (
            <section className="border rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">Completed</span>
              </div>
            </section>
          )}

          <DocumentUploader
            title="Application Documents"
            documents={record.application_documents || []}
            uploadPath={`/backoffice/licenses/${license.id}/records/${record.id}/application_documents`}
            deletePath={(docId) => `/backoffice/licenses/${license.id}/records/${record.id}/application_documents/${docId}`}
          />

          <DocumentUploader
            title="Filing Documents"
            documents={record.filing_documents || []}
            uploadPath={`/backoffice/licenses/${license.id}/records/${record.id}/filing_documents`}
            deletePath={(docId) => `/backoffice/licenses/${license.id}/records/${record.id}/filing_documents/${docId}`}
          />

          <DocumentUploader
            title="Completion Documents"
            documents={record.completion_documents || []}
            uploadPath={`/backoffice/licenses/${license.id}/records/${record.id}/completion_documents`}
            deletePath={(docId) => `/backoffice/licenses/${license.id}/records/${record.id}/completion_documents/${docId}`}
          />
        </div>
      </div>
    </BackofficeLayout>
  )
}
